import { fetchMenuData } from '../services/menuFetchUtils';

// ── The store (equivalent to Frontity's state.theme) ────────────────────────
const menuStore = {};

// ── Subscription system for reactive updates (per menuId) ───────────────────
// Subscribers per menuId so only components using that menu re-render when it loads.
const subscribersByMenuId = new Map();

/**
 * Subscribe to store updates for a specific menuId. Returns an unsubscribe function.
 * Only this subscriber is notified when that menuId's data arrives — avoids unnecessary re-renders.
 */
export const subscribe = (menuId, callback) => {
  if (!subscribersByMenuId.has(menuId)) {
    subscribersByMenuId.set(menuId, new Set());
  }
  subscribersByMenuId.get(menuId).add(callback);
  return () => {
    const set = subscribersByMenuId.get(menuId);
    if (set) {
      set.delete(callback);
      if (set.size === 0) subscribersByMenuId.delete(menuId);
    }
  };
};

/**
 * Notify only subscribers that care about this menuId.
 */
const notifySubscribers = (menuId) => {
  subscribersByMenuId.get(menuId)?.forEach(callback => callback());
};

/**
 * Read the raw menu data for a given menuId.
 * Returns the raw API response (not formatted).
 */
export const getMenuData = (menuId) => menuStore[menuId] || null;

// ── Fetch (uses shared menuFetchUtils) ──────────────────────────────────────

/**
 * Fetch all required menus and store the raw data.
 * Called once at initialization. All menu API calls run in parallel.
 */
const fetchAllMenusData = async () => {
  const menuIds = [
    'guest_navigation_menu',
    'customer_navigation_menu',
    'footer_menu',
    'quoting_footer_menu',
  ];

  // Fire all fetches in parallel; each resolves and notifies its subscribers when done
  const fetchPromises = menuIds.map(async (menuId) => {
    const data = await fetchMenuData(menuId);
    if (data) {
      menuStore[menuId] = data;
      notifySubscribers(menuId);
    }
  });

  await Promise.all(fetchPromises);
};

// ── Initialisation ──────────────────────────────────────────────────────────

/**
 * Initialise the store.
 * Fetches menu data from the configured source (Node.js server or WordPress).
 * This runs in the background and doesn't block React mounting.
 * Components will update reactively when data arrives.
 */
const initMenuStore = () => {
  fetchAllMenusData();
};

// Start fetching immediately (non-blocking)
initMenuStore();
